import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

// Interface for photo data
export interface PhotoData {
  base64String: string;
  format: string;
}

/**
 * Optimize image by resizing and compressing
 * High quality settings: 1600x1200 @ 90% WebP for sharp property documentation
 * WebP reduces file size by 70-80% while maintaining excellent visual quality
 */
function optimizeImage(
  dataUrl: string,
  maxWidth: number = 1600,
  maxHeight: number = 1200,
  quality: number = 0.90
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio);
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas and resize image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to WebP with compression (30% smaller than JPEG)
        const optimizedDataUrl = canvas.toDataURL('image/webp', quality);
        resolve(optimizedDataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

// Main class for camera service
export class CameraService {
  private static instance: CameraService;
  
  // Flag to track if our custom camera is available
  private hasCustomCameraSupport: boolean;
  
  private constructor() {
    // Check if web APIs needed for custom camera are available
    this.hasCustomCameraSupport = 
      typeof navigator !== 'undefined' && 
      !!navigator.mediaDevices && 
      !!navigator.mediaDevices.getUserMedia &&
      Capacitor.isNativePlatform(); // Only use custom camera on native platforms
  }
  
  public static getInstance(): CameraService {
    if (!CameraService.instance) {
      CameraService.instance = new CameraService();
    }
    
    return CameraService.instance;
  }
  
  /**
   * Check if custom camera is supported on this device
   */
  public supportsCustomCamera(): boolean {
    return this.hasCustomCameraSupport;
  }
  
  /**
   * Take multiple photos with standard Capacitor camera
   * This is the fallback method when custom camera isn't available
   */
  public async takeMultiplePhotosStandard(maxPhotos: number = 30): Promise<PhotoData[]> {
    const photos: PhotoData[] = [];
    
    try {
      // Use the standard Capacitor Camera API with limited settings
      const result = await Camera.pickImages({
        quality: 90,
        limit: maxPhotos
      });
      
      // Process and optimize the photos
      for (const photo of result.photos) {
        if (photo.webPath) {
          // Convert webPath to base64
          const response = await fetch(photo.webPath);
          const blob = await response.blob();
          const dataUrl = await this.blobToDataUrl(blob);
          
          // Optimize image (resize to 1600x1200, 90% quality)
          const optimizedDataUrl = await optimizeImage(dataUrl, 1600, 1200, 0.90);
          const base64 = optimizedDataUrl.split(',')[1];
          
          photos.push({
            base64String: base64,
            format: 'webp'
          });
        }
      }
      
      return photos;
    } catch (error) {
      console.error('Error taking photos with standard camera:', error);
      throw error;
    }
  }
  
  /**
   * Process photos from custom camera with optimization
   */
  public async processCustomCameraPhotos(dataUrls: string[]): Promise<PhotoData[]> {
    const processedPhotos: PhotoData[] = [];
    
    for (const dataUrl of dataUrls) {
      try {
        // Optimize image (resize to 1600x1200, 90% quality)
        const optimizedDataUrl = await optimizeImage(dataUrl, 1600, 1200, 0.90);
        const base64String = optimizedDataUrl.split(',')[1];
        
        processedPhotos.push({
          base64String,
          format: 'webp'
        });
      } catch (error) {
        console.error('Error optimizing photo:', error);
        // Fallback to original if optimization fails
        const base64String = dataUrl.split(',')[1];
        processedPhotos.push({
          base64String,
          format: 'webp'
        });
      }
    }
    
    return processedPhotos;
  }
  
  /**
   * Take a single photo with standard camera
   */
  public async takeSinglePhoto(): Promise<PhotoData> {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera
      });
      
      if (!image.base64String) {
        throw new Error('No image data returned');
      }
      
      // Optimize image (resize to 1600x1200, 90% quality)
      const dataUrl = `data:image/${image.format};base64,${image.base64String}`;
      const optimizedDataUrl = await optimizeImage(dataUrl, 1600, 1200, 0.90);
      const optimizedBase64 = optimizedDataUrl.split(',')[1];
      
      return {
        base64String: optimizedBase64,
        format: 'webp'
      };
    } catch (error) {
      console.error('Error taking single photo:', error);
      throw error;
    }
  }
  
  /**
   * Convert a Blob to data URL string
   */
  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to data URL'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export default CameraService; 